import React, { useState, useEffect, useRef, useMemo } from "react";

import { useNavigate, useLocation } from "react-router-dom";

import FollowButton from "../components/FollowButton";
import LeftMenu from "../components/LeftMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import apiService from "../services/api";

const SPORT_HERO_BACKGROUNDS = {
  nba: "/sports-bg-basketball.png",
  basketball: "/sports-bg-basketball.png",
  nfl: "/sports-bg-nfl.png",
  ncaaf: "/sports-bg-nfl.png",
  "american football": "/sports-bg-nfl.png",
  nhl: "/sports-bg-nhl.png",
  "ice hockey": "/sports-bg-nhl.png",
  hockey: "/sports-bg-nhl.png",
  mlb: "/sports-bg-mlb.png",
  baseball: "/sports-bg-mlb.png",
  soccer: "/sports-bg-soccer.png",
  football: "/sports-bg-soccer.png",
  mls: "/sports-bg-soccer.png",
  "premier league": "/sports-bg-soccer.png",
  default: "/sports-bg-basketball.png",
};

const WebPlayerFindChannel = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const event = useMemo(
    () => location.state?.event || null,
    [location.state?.event]
  );

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [streamUrl, setStreamUrl] = useState(null);
  const [streamTitle, setStreamTitle] = useState(null);

  const firstCardRef = useRef(null);
  const fetchChannelsRef = useRef(null);

  const sportKey = event?.sport?.toLowerCase() || "default";
  const heroBg =
    SPORT_HERO_BACKGROUNDS[sportKey] || SPORT_HERO_BACKGROUNDS.default;

  useEffect(() => {
    if (!event) {
      setLoading(false);

      return;
    }
    fetchChannelsRef.current?.();
  }, [event]);

  useEffect(() => {
    if (!loading && channels.length > 0 && firstCardRef.current) {
      firstCardRef.current.focus();
    }
  }, [loading, channels]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      const mediaId = event?.id;

      if (!mediaId) {
        setLoading(false);

        return;
      }

      const sport = event?.sport;
      const endpoint = sport
        ? `/v2/sports/events/live/${mediaId}?sport=${sport.toLowerCase()}`
        : `/v2/sports/events/live/${mediaId}`;

      const response = await apiService.webPlayerRequest(endpoint);
      const liveTvChannels = response?.data?.liveTv || response?.liveTv || [];

      const mappedChannels = liveTvChannels.map((channel) => {
        const currentProgram = channel.programmes?.[0];

        return {
          ...channel,
          stream_url: channel.streamUrl,
          stream_icon: channel.logoPath || channel.posterPath,
          epg_channel_id: channel.epgChannelId,
          currentProgram: currentProgram
            ? {
                title: currentProgram.title,
                description: currentProgram.description,
                start: new Date(currentProgram.start).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                stop: new Date(currentProgram.stop).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
              }
            : null,
        };
      });

      setChannels(mappedChannels);
    } catch (error) {
      console.error("Error fetching channels:", error);
      setChannels([]);
    } finally {
      setLoading(false);
    }
  };

  fetchChannelsRef.current = fetchChannels;

  const handlePlayChannel = (channel) => {
    let url = channel.stream_url || channel.streamUrl;

    if (!url) return;

    try {
      const decoded = atob(url);

      if (decoded.startsWith("http")) {
        url = decoded;
      }
    } catch {
      // not base64, use as-is
    }

    setStreamUrl(url);
    setStreamTitle(channel.name || channel.stream_display_name || "Channel");
    setPlayerOpen(true);
  };

  // No event data passed — show error
  if (!event) {
    return (
      <div className="flex min-h-screen">
        <LeftMenu />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <i className="fas fa-exclamation-triangle text-5xl text-yellow-500/60 mb-4" />
            <p className="text-white/60 text-lg mb-6">
              No game data available.
            </p>
            <button
              className="px-6 py-3 bg-white/10 hover:bg-white/15 text-white rounded-xl transition-colors"
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <LeftMenu />

      <div className="flex-1 relative overflow-hidden">
        {/* Sport background image */}
        <div className="absolute inset-0 z-0">
          <img
            alt=""
            className="w-full h-full object-cover opacity-15"
            src={heroBg}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/60 via-[#0a0a0f]/80 to-[#0a0a0f]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0f]/50 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 px-8 py-8 max-w-6xl mx-auto">
          {/* Top bar: Back + League pill */}
          <div className="flex items-center justify-between mb-12">
            <button
              className="flex items-center gap-2 text-white/60 hover:text-white transition-colors group"
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-arrow-left text-sm group-hover:-translate-x-1 transition-transform" />
              <span className="text-sm font-medium">Back</span>
            </button>

            {event.strLeague && (
              <span className="px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full text-white/70 text-xs font-medium border border-white/10">
                {event.strLeague}
              </span>
            )}
          </div>

          {/* Matchup header */}
          <div className="flex items-center justify-center gap-8 mb-4">
            {/* Away team */}
            <div className="flex flex-col items-center gap-3">
              {event.strAwayTeamBadge ? (
                <img
                  alt={event.strAwayTeam}
                  className="w-24 h-24 object-contain drop-shadow-2xl"
                  src={event.strAwayTeamBadge}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
                  <i className="fas fa-shield-alt text-3xl text-white/30" />
                </div>
              )}
              <span className="text-white/80 text-base font-medium text-center max-w-[140px]">
                {event.strAwayTeam || "Away"}
              </span>
              {event.strAwayTeam && (
                <FollowButton
                  entity={event.strAwayTeam}
                  size="sm"
                  type="team"
                  variant="pill"
                />
              )}
            </div>

            {/* Score */}
            <div className="flex flex-col items-center">
              <div className="text-5xl font-bold text-white tracking-wider">
                {event.awayTeamScore != null && event.homeTeamScore != null ? (
                  <>
                    <span>{event.awayTeamScore}</span>
                    <span className="text-white/30 mx-3">–</span>
                    <span>{event.homeTeamScore}</span>
                  </>
                ) : (
                  <span className="text-white/30 text-3xl">vs</span>
                )}
              </div>
              {event.timestamp && (
                <span className="text-white/40 text-sm mt-2 font-medium">
                  {event.timestamp}
                </span>
              )}
            </div>

            {/* Home team */}
            <div className="flex flex-col items-center gap-3">
              {event.strHomeTeamBadge ? (
                <img
                  alt={event.strHomeTeam}
                  className="w-24 h-24 object-contain drop-shadow-2xl"
                  src={event.strHomeTeamBadge}
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
                  <i className="fas fa-shield-alt text-3xl text-white/30" />
                </div>
              )}
              <span className="text-white/80 text-base font-medium text-center max-w-[140px]">
                {event.strHomeTeam || "Home"}
              </span>
              {event.strHomeTeam && (
                <FollowButton
                  entity={event.strHomeTeam}
                  size="sm"
                  type="team"
                  variant="pill"
                />
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/10 my-10" />

          {/* Channels section */}
          <h2 className="text-white text-xl font-semibold mb-6">
            <i className="fas fa-satellite-dish mr-3 text-blue-400" />
            Channels Playing This Game
          </h2>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <i className="fas fa-spinner fa-spin text-3xl text-white/30" />
            </div>
          ) : channels.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {channels.map((channel, idx) => (
                <button
                  className="group relative bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 hover:border-blue-500/40 rounded-2xl p-4 flex items-center gap-4 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/40 focus:bg-white/[0.1] focus:scale-[1.01]"
                  key={channel.epg_channel_id || channel.name || idx}
                  ref={idx === 0 ? firstCardRef : null}
                  tabIndex={0}
                  onClick={() => handlePlayChannel(channel)}
                >
                  {/* Channel logo */}
                  <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {channel.stream_icon ? (
                      <img
                        alt={channel.name}
                        className="w-full h-full object-contain p-1.5"
                        src={channel.stream_icon}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <div
                      className={`w-full h-full items-center justify-center ${channel.stream_icon ? "hidden" : "flex"}`}
                    >
                      <i className="fas fa-tv text-xl text-white/30" />
                    </div>
                  </div>

                  {/* Channel info */}
                  <div className="flex-1 min-w-0 text-left">
                    <span className="text-white/90 text-sm font-semibold block truncate">
                      {channel.name ||
                        channel.stream_display_name ||
                        "Unknown Channel"}
                    </span>
                    {channel.currentProgram && (
                      <span className="text-white/40 text-xs block truncate mt-0.5">
                        {channel.currentProgram.title}
                      </span>
                    )}
                    {channel.currentProgram?.start && (
                      <span className="text-white/25 text-[11px] block mt-0.5">
                        {channel.currentProgram.start} –{" "}
                        {channel.currentProgram.stop}
                      </span>
                    )}
                  </div>

                  {/* Watch CTA */}
                  <span className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600/80 group-hover:bg-blue-500 group-focus:bg-blue-500 text-white text-xs font-semibold rounded-lg transition-colors flex-shrink-0">
                    <i className="fas fa-play text-[10px]" />
                    Watch
                  </span>
                </button>
              ))}
            </div>
          ) : (
            /* Empty state */
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-5">
                <i className="fas fa-satellite-dish text-3xl text-white/20" />
              </div>
              <p className="text-white/50 text-lg font-medium mb-2">
                No channels broadcasting this game
              </p>
              <p className="text-white/30 text-sm">
                Check back closer to kickoff for available channels.
              </p>
            </div>
          )}
        </div>

        {/* Video player overlay */}
        {playerOpen && streamUrl && (
          <div className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center">
            <div className="relative w-full max-w-5xl mx-4">
              <button
                className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors z-10"
                onClick={() => {
                  setPlayerOpen(false);
                  setStreamUrl(null);
                  setStreamTitle(null);
                }}
              >
                <i className="fas fa-times text-xl mr-2" />
                Close
              </button>
              {streamTitle && (
                <p className="absolute -top-12 left-0 text-white/60 text-sm font-medium">
                  {streamTitle}
                </p>
              )}
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <PlyrVideoPlayer
                  autoplay
                  sourceUrl={streamUrl}
                  title={streamTitle}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebPlayerFindChannel;
